import { useState } from "react";
import { api, ApiError } from "../api";
import type { Turbine, TurbineStartBody, TurbineStopBody } from "../types";
import { tg } from "../telegram";
import { useT } from "../i18n";

interface Props {
  turbine: Turbine;
  onChanged: () => void;
}

/**
 * Pair of big Start / Stop buttons for one turbine, with inline mini-forms
 * for KW and (on stop) reason + event time. Used both by responsibles on
 * the Report page and by admins on the Turbines page.
 */
export default function TurbineActions({ turbine, onChanged }: Props) {
  const { t } = useT();
  const [mode, setMode] = useState<"none" | "start" | "stop">("none");
  // Reused across both forms: holds KV on start, KW on stop.
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  // datetime-local input — keep as string until submit
  const [eventTime, setEventTime] = useState(() => nowLocal());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMode("none");
    setValue("");
    setReason("");
    setEventTime(nowLocal());
    setError(null);
  };

  const submitStart = async () => {
    if (!value.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
    try {
      const body: TurbineStartBody = { kv: value.trim() };
      await api.post<Turbine>(`/api/turbines/${turbine.id}/start`, body);
      tg()?.HapticFeedback?.notificationOccurred("success");
      reset();
      onChanged();
    } catch (e) {
      setError((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitStop = async () => {
    if (!value.trim() || !reason.trim() || !eventTime || submitting) return;
    setSubmitting(true);
    setError(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
    try {
      const body: TurbineStopBody = {
        kw: value.trim(),
        reason: reason.trim(),
        // datetime-local has no timezone — interpret in the user's local tz
        // and send as ISO with offset.
        event_time: localToIso(eventTime),
      };
      await api.post<Turbine>(`/api/turbines/${turbine.id}/stop`, body);
      tg()?.HapticFeedback?.notificationOccurred("success");
      reset();
      onChanged();
    } catch (e) {
      setError((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!turbine.is_active) {
    return null;  // deactivated turbine: no Start/Stop possible
  }

  // STOPPED → show Start button (and form when opened)
  if (!turbine.is_running) {
    if (mode === "start") {
      return (
        <div className="card turbine-action-form">
          <h3 style={{ marginTop: 0 }}>▶ {t("turbines.start_title", { name: turbine.name })}</h3>
          {error && <div className="alert error">{error}</div>}
          <label className="report-form-row" style={{ display: "block", padding: "8px 12px", marginBottom: 8 }}>
            <div className="report-form-label">KV <span style={{ color: "var(--destructive)" }}>*</span></div>
            <input
              className="report-form-input"
              type="text" inputMode="decimal" placeholder="—"
              value={value} onChange={e => setValue(e.target.value)} autoFocus
              style={{ width: "100%" }}
            />
          </label>
          <div className="row">
            <button className="btn" onClick={submitStart} disabled={!value.trim() || submitting}>
              {submitting ? t("report_form.submitting") : t("turbines.confirm_start")}
            </button>
            <button className="btn secondary" onClick={reset} disabled={submitting}>{t("common.cancel")}</button>
          </div>
        </div>
      );
    }
    return (
      <button className="big-btn primary" onClick={() => { reset(); setMode("start"); }}>
        <span className="big-btn-icon">▶</span>
        <span className="big-btn-text">
          <b>{t("turbines.start_btn")}</b>
          <small>{t("turbines.start_sub")}</small>
        </span>
      </button>
    );
  }

  // RUNNING → show Stop button (and form when opened)
  if (mode === "stop") {
    return (
      <div className="card turbine-action-form">
        <h3 style={{ marginTop: 0 }}>⏹ {t("turbines.stop_title", { name: turbine.name })}</h3>
        {error && <div className="alert error">{error}</div>}
        <label className="report-form-row" style={{ display: "block", padding: "8px 12px", marginBottom: 8 }}>
          <div className="report-form-label">KW <span style={{ color: "var(--destructive)" }}>*</span></div>
          <input
            className="report-form-input"
            type="text" inputMode="decimal" placeholder="—"
            value={value} onChange={e => setValue(e.target.value)} autoFocus
            style={{ width: "100%" }}
          />
        </label>
        <label className="report-form-row" style={{ display: "block", padding: "8px 12px", marginBottom: 8 }}>
          <div className="report-form-label">{t("turbines.stop_time")} <span style={{ color: "var(--destructive)" }}>*</span></div>
          <input
            type="datetime-local"
            value={eventTime}
            max={nowLocal()}
            onChange={e => setEventTime(e.target.value)}
            style={{ width: "100%" }}
          />
        </label>
        <label className="report-form-row" style={{ display: "block", padding: "8px 12px", marginBottom: 8 }}>
          <div className="report-form-label">{t("turbines.stop_reason")} <span style={{ color: "var(--destructive)" }}>*</span></div>
          <textarea
            value={reason} onChange={e => setReason(e.target.value)}
            placeholder={t("turbines.stop_reason_placeholder")}
            maxLength={500}
            rows={3}
            style={{ width: "100%", resize: "vertical", minHeight: 70 }}
          />
        </label>
        <div className="row">
          <button className="btn danger" onClick={submitStop} disabled={!value.trim() || !reason.trim() || !eventTime || submitting}>
            {submitting ? t("report_form.submitting") : t("turbines.confirm_stop")}
          </button>
          <button className="btn secondary" onClick={reset} disabled={submitting}>{t("common.cancel")}</button>
        </div>
      </div>
    );
  }
  return (
    <button className="big-btn danger-outline" onClick={() => { reset(); setMode("stop"); }}>
      <span className="big-btn-icon">⏹</span>
      <span className="big-btn-text">
        <b>{t("turbines.stop_btn")}</b>
        <small>{t("turbines.stop_sub")}</small>
      </span>
    </button>
  );
}

function nowLocal(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    "-", String(d.getMonth() + 1).padStart(2, "0"),
    "-", String(d.getDate()).padStart(2, "0"),
    "T", String(d.getHours()).padStart(2, "0"),
    ":", String(d.getMinutes()).padStart(2, "0"),
  ].join("");
}

function localToIso(localStr: string): string {
  // datetime-local is "YYYY-MM-DDTHH:MM" with no timezone. new Date(localStr)
  // interprets in the user's local timezone — that matches what the operator
  // means by "I stopped it at 14:00".
  return new Date(localStr).toISOString();
}
